/**
 * TRUS-732: per-program Case assignment.
 *
 * A super admin sets on each program which field is the case manager of record,
 * what the role is called on screen, when a case is closed, and whether open
 * cases whose case manager is no longer on the list are flagged. The list comes
 * from staff setup (a tick on the staff member's program assignment), so
 * removing someone from the org takes them off every list in one step.
 *
 * Display only — nothing here changes who can see or edit a member.
 */
import { apiClient } from '@/services/apiClient';

export type AssigneeField = 'enrollment' | 'member_case_manager';

export type ClosedRule =
  | { source: 'enrollment_status' }
  | { source: 'custom_field'; field_id: number; closed_values: string[] };

export interface CaseAssignmentConfig {
  enabled: boolean;
  label: string;
  assignee_field: AssigneeField;
  closed_rule: ClosedRule;
  flag_reassignment: boolean;
}

export const DEFAULT_CASE_ASSIGNMENT: CaseAssignmentConfig = {
  enabled: true,
  label: 'Case Manager',
  assignee_field: 'enrollment',
  closed_rule: { source: 'enrollment_status' },
  flag_reassignment: true,
};

export interface CaseManagerOption {
  user_id: number;
  name: string;
  email?: string | null;
}

export interface MemberCaseAssignment {
  program_id: number;
  program_name: string;
  enrollment_id: number;
  label: string;
  assignee_field: AssigneeField;
  assignee_user_id: number | null;
  assignee_name: string | null;
  assignee_on_list: boolean;
  case_open: boolean;
  needs_reassignment: boolean;
}

export interface CaseManagerAssignmentFlag {
  assignment_id: number;
  program_id: number;
  is_case_manager: boolean;
}

export const caseAssignmentAPI = {
  async getConfig(programId: number): Promise<CaseAssignmentConfig | null> {
    const { data } = await apiClient.get(`/api/programs/${programId}/case-assignment`);
    return data?.config ?? null;
  },

  /** Super admins only. `null` clears the setting. */
  async setConfig(programId: number, config: CaseAssignmentConfig | null): Promise<CaseAssignmentConfig | null> {
    const { data } = await apiClient.put(`/api/programs/${programId}/case-assignment`, config);
    return data?.config ?? null;
  },

  async listCaseManagers(programId: number): Promise<CaseManagerOption[]> {
    const { data } = await apiClient.get(`/api/programs/${programId}/case-managers`);
    return Array.isArray(data) ? data : [];
  },

  async getUserFlags(userId: number): Promise<CaseManagerAssignmentFlag[]> {
    const { data } = await apiClient.get(`/api/users/${userId}/case-manager-assignments`);
    return Array.isArray(data) ? data : [];
  },

  async setUserFlag(userId: number, assignmentId: number, isCaseManager: boolean): Promise<void> {
    await apiClient.put(`/api/users/${userId}/program-assignments/${assignmentId}/case-manager`, {
      is_case_manager: isCaseManager,
    });
  },

  async getMemberAssignments(memberId: number): Promise<MemberCaseAssignment[]> {
    const { data } = await apiClient.get(`/api/members/${memberId}/case-assignments`);
    return Array.isArray(data) ? data : [];
  },

  async assign(memberId: number, programId: number, userId: number | null) {
    const { data } = await apiClient.put(`/api/members/${memberId}/case-assignments/${programId}`, {
      user_id: userId,
    });
    return data as { assignee_user_id: number | null; assignee_name: string | null };
  },
};
